// Signing in at login.kast-dev.net, the one sign-in site every kast-dev app
// shares, and out again. The site signs the user in and sends the browser
// back to the page it was sent from, with the tokens in the URL's hash;
// completeSignIn (run before the first render) hands them to the shared
// session (./session.ts). The page to come back to rides in the sign-in URL.

import {
  hostedUiLogoutUrl,
  navigateTo,
  redirectToSignIn,
  takeTokensFromUrl,
} from "@jkast90/kast-common/auth";
import { getCognitoClientId, getCognitoDomain } from "../_common/settings";
import { logger } from "../_common/utils/logger";
import { session } from "./session";

/** Set when tokens have just come back from the sign-in site; FirstSignIn reads it once. */
export const JUST_SIGNED_IN_KEY = "kindlyJustSignedIn";

/** Off to the sign-in site, coming back to this page. */
export function startSignIn(): void {
  redirectToSignIn();
}

/**
 * Back from the sign-in site: store the hash's tokens in the session and
 * scrub them from the URL, then move to where the user was going. Run before
 * the first render, so the first frame is already signed in.
 */
export function completeSignIn(): void {
  const taken = takeTokensFromUrl(session);
  if (!taken) return;
  try {
    sessionStorage.setItem(JUST_SIGNED_IN_KEY, "1");
  } catch {
    // Storage blocked: a new user just isn't taken to their profile.
  }
  if (taken.returnTo) window.history.replaceState(null, "", taken.returnTo);
}

/**
 * Sign out at Cognito: the session revokes the refresh token and clears the
 * store, tells its subscribers (session.ts clears the user's data; the Redux
 * mirror skips the `leaving` state), then the page leaves through the hosted
 * UI's logout, without first showing its signed-out states.
 */
export function signOut(): Promise<void> {
  logger.auth("Logout initiated by user");
  return session.signOut({
    leave: () =>
      navigateTo(
        hostedUiLogoutUrl({
          cognitoDomain: getCognitoDomain(),
          clientId: getCognitoClientId(),
          logoutUri: window.location.origin,
        }),
        { replace: true },
      ),
  });
}
